'use client';

import Icon from '@/components/ui/AppIcon';
import CountUp from 'react-countup';

interface Metric {
  icon: string;
  value: string;
  label: string;
  description: string;
}

interface ImpactMetricsProps {
  className?: string;
}

const ImpactMetrics = ({ className = '' }: ImpactMetricsProps) => {
  const metrics: Metric[] = [
    {
      icon: 'BriefcaseIcon',
      value: '500,000+',
      label: 'Jobs Created',
      description: 'Direct and indirect employment across all sectors by 2044',
    },
    {
      icon: 'CurrencyDollarIcon',
      value: '$5B+',
      label: 'GDP Contribution',
      description: 'Annual economic impact at full operational maturity',
    },
    {
      icon: 'BuildingOffice2Icon',
      value: '10+',
      label: 'Industrial Parks',
      description: 'Modern infrastructure facilities across East Africa',
    },
    {
      icon: 'UserGroupIcon',
      value: '2M+',
      label: 'Lives Impacted',
      description: 'Community members benefiting from economic transformation',
    },
    {
      icon: 'ChartBarSquareIcon',
      value: '12%-15%',
      label: 'Target Returns',
      description: 'Projected IRR for institutional investors over 20 years',
    },
    {
      icon: 'GlobeAltIcon',
      value: '30%',
      label: 'Local Ownership',
      description: 'Community equity participation in mature operations',
    },
  ];

  return (
    <section data-header="light" className={`py-4 bg-white ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-6 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-black/5 px-4 py-1 sm:py-2 rounded-full mb-2 sm:mb-4 md:mb-6">
            <Icon name="ChartBarIcon" size={20} variant="solid" className="text-success" />
            <span className="font-body text-xs sm:text-sm font-medium text-success">Projected Impact</span>
          </div>

          <h2 className="font-headline text-4xl sm:text-5xl font-bold text-black/90 mb-2 sm:mb-4 ">
            Measurable Outcomes
          </h2>

          <p className="font-body text-base sm:text-lg text-black/50 max-w-4xl mx-auto">
            Quantified projections demonstrating the scale and sustainability of our transformation
            agenda across economic, social, and financial dimensions.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {metrics.map((metric, index) => {
            const isRange = metric.value.includes('-') || metric.value.includes('–');

            // Handle suffix multipliers like K, M, B
            let numeric: number | null = null;
            let suffix = '';

            if (!isRange) {
              const match = metric.value.match(/([\d,.]+)\s*([KMB]?)/i);
              if (match) {
                let num = parseFloat(match[1].replace(/,/g, ''));
                const unit = match[2].toUpperCase();
                if (unit === 'K') num *= 1_000;
                if (unit === 'M') num *= 1_000_000;
                if (unit === 'B') num *= 1_000_000_000;
                numeric = num;

                // Keep symbols like $ and + in suffix
                suffix = metric.value.replace(/[\d.,KMB]/gi, '');
              }
            }

            return (
              <div
                key={index}
                className="bg-card border border-border p-4 sm:p-6 rounded-lg hover:shadow-institutional transition-all duration-300"
              >
                <div className="hidden sm:flex items-center justify-center w-14 h-14 bg-primary/10 rounded-lg mb-2 sm:mb-4 ">
                  <Icon
                    name={metric.icon as any}
                    size={28}
                    variant="outline"
                    className="text-primary"
                  />
                </div>

                <div className="font-headline text-3xl font-semibold text-foreground mb-2">
                  {isRange || numeric === null ? (
                    metric.value
                  ) : (
                    <>
                      <CountUp end={numeric} duration={20} separator="," />
                      {suffix}
                    </>
                  )}
                </div>

                <div className="font-body text-base font-medium text-text-primary mb-2">
                  {metric.label}
                </div>

                <p className="font-body text-sm text-text-secondary leading-relaxed">
                  {metric.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImpactMetrics;
